import { isValidEmail } from "../../../Shared/Utilities/string";
import { VALIDATION } from "./provetConstants";

/**
 * ProvetValidationUtilities provides static methods for validating Provet form fields and Swedish identification numbers.
 */
export class ProvetValidationUtilities {
  /** Regex for Swedish identification number (personnummer) format. */
  public static readonly SwedishIdentificationNumberRegex = /^(\d{6}|\d{8})-?\d{4}$/;

  /**
   * Validates Swedish identification number (personnummer) format.
   * @param identificationNumber - The identification number to validate
   * @returns True if the format is valid, false otherwise
   */
  public static isValidSwedishIdentificationNumber(
    identificationNumber: string
  ): boolean {
    if (!identificationNumber || identificationNumber.trim() === "") {
      return false;
    }
    return ProvetValidationUtilities.SwedishIdentificationNumberRegex.test(
      identificationNumber.trim()
    );
  }

  /**
   * Splits identification number (Swedish personnummer) in all possible formats.
   *
   * Accepted formats of identification number:
   * ----------------
   * 720111-0000
   * 7201110000
   * 19720111-0000
   * 197201110000
   *
   * Identification number combinations that will be returned in string array:
   * ----------------
   * 197201110000 =>
   *     7201110000
   *     720111-0000
   *     19720111-0000
   *     197201110000
   *
   * @param identificationNumber - The identification number to process
   * @returns An array of identification number combinations, or null if invalid
   */
  public static getIdentificationNumberCombinations(
    identificationNumber: string
  ): string[] | null {
    if (!identificationNumber || identificationNumber.trim() === "") {
      return null;
    }

    const trimmedId = identificationNumber.trim();

    if (ProvetValidationUtilities.SwedishIdentificationNumberRegex.test(trimmedId)) {
      let processedId = trimmedId;

      // Check if century is missing, if so, add century digits...
      if (processedId.length <= 11) {
        const centuryRegex = /^(\d{2})(\d{2})(\d{2})-?(\d{4})$/;
        const centuryMatches = centuryRegex.exec(processedId);

        if (centuryMatches) {
          const yearString = centuryMatches[1];
          const currentYear = new Date().getFullYear();
          const currentYearTwoDigit = parseInt(currentYear.toString().slice(-2));

          const givenYearTwoDigit = parseInt(yearString);

          // Calculate century based on comparison with current year
          const century = givenYearTwoDigit > currentYearTwoDigit ? "19" : "20";

          processedId = `${century}${centuryMatches[1]}${centuryMatches[2]}${centuryMatches[3]}-${centuryMatches[4]}`;
        }
      }

      const identificationNumberRegex = /^(\d{2})(\d{6})-?(\d{4})$/;
      const matches = identificationNumberRegex.exec(processedId);

      if (matches) {
        return [
          `${matches[2]}-${matches[3]}`, // 720111-0000
          `${matches[2]}${matches[3]}`, // 7201110000
          `${matches[1]}${matches[2]}-${matches[3]}`, // 19720111-0000
          `${matches[1]}${matches[2]}${matches[3]}` // 197201110000
        ];
      }
    } else {
      // Match directly to identification number if it's not Swedish
      return [trimmedId];
    }

    return null;
  }

  /**
   * Creates a comprehensive regex pattern that matches all valid Swedish identification number formats.
   * @returns A regex pattern string that can be used for validation
   */
  public static getComprehensiveSwedishIdentificationNumberRegex(): string {
    // This regex matches all the formats that the C# code accepts:
    // - 6 digits + dash + 4 digits (YYMMDD-NNNN)
    // - 6 digits + 4 digits (YYMMDDNNNN)
    // - 8 digits + dash + 4 digits (YYYYMMDD-NNNN)
    // - 8 digits + 4 digits (YYYYMMDDNNNN)
    return "^(19|20)?\\d{2}(0[1-9]|1[0-2])(0[1-9]|[12]\\d|3[01])-?\\d{4}$";
  }

  /**
   * Validates using the comprehensive regex pattern.
   * @param identificationNumber - The identification number to validate
   * @returns True if valid according to comprehensive validation
   */
  public static validateComprehensiveSwedishIdentificationNumber(
    identificationNumber: string
  ): boolean {
    if (!identificationNumber || identificationNumber.trim() === "") {
      return false;
    }

    const comprehensiveRegex = new RegExp(
      ProvetValidationUtilities.getComprehensiveSwedishIdentificationNumberRegex()
    );
    return comprehensiveRegex.test(identificationNumber.trim());
  }

  /**
   * Checks if a value is present (non-empty).
   * @param value - The string value to check
   * @returns True if the value is non-empty, false otherwise
   */
  public static isRequired(value?: string): boolean {
    return !!value && value.trim().length > 0;
  }

  /**
   * Validates email format and length (Provet API requirement: max 254 chars).
   * Uses Django EmailField validation standard.
   * @param email - The email string to validate
   * @returns True if the email is valid, false otherwise
   */
  public static isValidEmail(email?: string): boolean {
    if (!email) {
      return false;
    }

    const trimmed = email.trim();

    // Check max length (Django EmailField limit)
    if (trimmed.length > VALIDATION.MAX_CLIENT_EMAIL_LENGTH) {
      return false;
    }

    return isValidEmail(trimmed);
  }

  /**
   * Validates phone number format and length (Provet API requirement: max 30 chars).
   * Requires sufficient digits for a valid phone number and allows spaces, plus, parentheses, and hyphens.
   * Note: Provet API automatically strips whitespace and dashes.
   * @param phone - The phone string to validate
   * @returns True if the phone is valid, false otherwise
   */
  public static isValidPhone(phone?: string): boolean {
    if (!phone) {
      return false;
    }

    const trimmed = phone.trim();

    // Check max length (Provet API limit)
    if (trimmed.length > VALIDATION.MAX_CLIENT_PHONE_LENGTH) {
      return false;
    }

    // Must contain valid phone characters only
    if (!/^[\d\s()+-]+$/.test(trimmed)) {
      return false;
    }

    // Extract just the digits to analyze the number
    const digits = trimmed.replace(/\D/g, "");

    // Minimum realistic phone number length:
    // - International: country code (1-4 digits) + local number (6-10 digits) = at least 7 digits
    // - Local: typically 6-10 digits
    // Using 7 as the minimum ensures we don't accept just a country code
    return digits.length >= 7;
  }

  /**
   * Validates identification number format and length (Provet API requirement: max 63 chars).
   * @param id - The identification number to validate
   * @returns True if the identification number is valid, false otherwise
   */
  public static isValidIdentificationNumber(id?: string): boolean {
    if (!id) {
      return false;
    }

    const trimmed = id.trim();

    // Check max length (Provet API limit)
    if (trimmed.length > VALIDATION.MAX_CLIENT_ID_NUMBER_LENGTH) {
      return false;
    }

    return ProvetValidationUtilities.isRequired(trimmed);
  }

  /**
   * Validates zip code (Provet API: optional, max 12 chars).
   * @param zipCode - The zip code to validate
   * @returns True if the zip code is valid, false otherwise
   */
  public static isValidZipCode(zipCode?: string): boolean {
    // Optional field - empty is valid
    if (!zipCode || zipCode.trim() === "") {
      return true;
    }

    return this.isValidLength(zipCode, VALIDATION.MAX_CLIENT_ZIP_CODE_LENGTH);
  }

  /**
   * Validates notes (non-empty and within length limit).
   * @param notes - The notes string to validate
   * @returns True if the notes are non-empty, false otherwise
   */
  public static isValidNotes(notes?: string): boolean {
    return ProvetValidationUtilities.isRequired(notes);
  }

  /**
   * Generic regex validation.
   * @param value - The string value to test
   * @param pattern - The regex pattern string
   * @returns True if the value matches the pattern, false otherwise
   */
  public static matchesRegex(value?: string, pattern?: string): boolean {
    if (!value || !pattern) {
      return false;
    }

    try {
      return new RegExp(pattern).test(value);
    } catch {
      return false;
    }
  }

  /**
   * Validates maximum length of a string.
   * @param value - The string value to check
   * @param max - The maximum allowed length
   * @returns True if the value's length is within the limit, false otherwise
   */
  public static isValidLength(value: string | undefined, max: number): boolean {
    if (!value) {
      return true;
    }
    return value.length <= max;
  }

  // =============================================================================
  // Provet API-specific validation methods
  // =============================================================================

  /**
   * Validates client first name (Provet API: required, max 100 chars).
   * @param firstName - The first name to validate
   * @returns True if valid, false otherwise
   */
  public static isValidClientFirstName(firstName?: string): boolean {
    return (
      ProvetValidationUtilities.isRequired(firstName) &&
      ProvetValidationUtilities.isValidLength(
        firstName,
        VALIDATION.MAX_CLIENT_FIRST_NAME_LENGTH
      )
    );
  }

  /**
   * Validates client last name (Provet API: required, max 100 chars).
   * @param lastName - The last name to validate
   * @returns True if valid, false otherwise
   */
  public static isValidClientLastName(lastName?: string): boolean {
    return (
      ProvetValidationUtilities.isRequired(lastName) &&
      ProvetValidationUtilities.isValidLength(
        lastName,
        VALIDATION.MAX_CLIENT_LAST_NAME_LENGTH
      )
    );
  }

  /**
   * Validates client street address (Provet API: optional, max 100 chars).
   * @param streetAddress - The street address to validate
   * @returns True if valid, false otherwise
   */
  public static isValidClientStreetAddress(streetAddress?: string): boolean {
    // Optional field - empty is valid
    if (!streetAddress || streetAddress.trim() === "") {
      return true;
    }
    return ProvetValidationUtilities.isValidLength(
      streetAddress,
      VALIDATION.MAX_CLIENT_STREET_ADDRESS_LENGTH
    );
  }

  /**
   * Validates client city (Provet API: optional, max 100 chars).
   * @param city - The city to validate
   * @returns True if valid, false otherwise
   */
  public static isValidClientCity(city?: string): boolean {
    // Optional field - empty is valid
    if (!city || city.trim() === "") {
      return true;
    }
    return ProvetValidationUtilities.isValidLength(
      city,
      VALIDATION.MAX_CLIENT_CITY_LENGTH
    );
  }

  /**
   * Validates patient name (Provet API: required, max 100 chars).
   * @param name - The patient name to validate
   * @returns True if valid, false otherwise
   */
  public static isValidPatientName(name?: string): boolean {
    return (
      ProvetValidationUtilities.isRequired(name) &&
      ProvetValidationUtilities.isValidLength(name, VALIDATION.MAX_PATIENT_NAME_LENGTH)
    );
  }

  /**
   * Validates patient notes (required, not just whitespace, max 1000 chars). Provet itself
   * doesn't require notes, but clinics rely on them, and the booking API requires them too.
   * @param notes - The patient notes to validate
   * @returns True if valid, false otherwise
   */
  public static isValidPatientNotes(notes?: string): boolean {
    if (!ProvetValidationUtilities.isRequired(notes)) {
      return false;
    }
    return ProvetValidationUtilities.isValidLength(
      notes,
      VALIDATION.MAX_PATIENT_NOTES_LENGTH
    );
  }

  /**
   * Validates patient microchip (Provet API: optional, max 20 chars).
   * @param microchip - The microchip number to validate
   * @returns True if valid, false otherwise
   */
  public static isValidPatientMicrochip(microchip?: string): boolean {
    // Optional field - empty is valid
    if (!microchip || microchip.trim() === "") {
      return true;
    }
    return ProvetValidationUtilities.isValidLength(
      microchip,
      VALIDATION.MAX_PATIENT_MICROCHIP_LENGTH
    );
  }
}
